import { Controller } from '@hotwired/stimulus'

/**
 * Broadcast Deduplication Controller
 *
 * Prevents duplicate rendering when using the "direct append + broadcast with lazy load" pattern.
 *
 * Usage:
 *   Add to any container element that will receive both direct turbo_stream responses
 *   and turbo_stream broadcasts that might create duplicate frames.
 *
 *   <div data-controller="broadcast-dedup"
 *        data-broadcast-dedup-debug-value="false">
 *     <!-- Frames will be appended/prepended here -->
 *   </div>
 *
 * How it works:
 *   1. When a user creates a resource, they get immediate feedback via turbo_stream.append
 *      with a frame marked as "loaded" (data-broadcast-dedup-loaded="true")
 *   2. A broadcast fires to all windows with a lazy-loading frame (has src attribute)
 *   3. This controller intercepts the broadcast and checks if a frame with that ID
 *      already exists and is marked as loaded
 *   4. If loaded, prevents the broadcast from rendering (no duplicate, no unnecessary fetch)
 *   5. If not loaded, allows the broadcast through (frame will lazy-load via src)
 */
export default class extends Controller {
  static values = {
    debug: { type: Boolean, default: false }
  }

  connect() {
    this.log('Controller connected')
    // Listen for turbo stream renders globally
    this.boundCheckDuplicate = this.checkForDuplicate.bind(this)
    document.addEventListener('turbo:before-stream-render', this.boundCheckDuplicate)
  }

  disconnect() {
    this.log('Controller disconnected')
    document.removeEventListener('turbo:before-stream-render', this.boundCheckDuplicate)
  }

  checkForDuplicate(event) {
    const streamElement = event.target
    const action = streamElement.getAttribute('action')

    // Only handle append and prepend actions
    if (action !== 'append' && action !== 'prepend') {
      return
    }

    // Get the template content
    const template = streamElement.querySelector('template')
    if (!template) return

    // Parse the template to find turbo-frames
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = template.innerHTML
    const incomingFrames = tempDiv.querySelectorAll('turbo-frame[data-broadcast-dedup-frame-id]')

    if (incomingFrames.length === 0) return

    incomingFrames.forEach(incomingFrame => {
      const frameId = incomingFrame.getAttribute('data-broadcast-dedup-frame-id')
      if (!frameId) return

      // Check if we already have a frame with this ID that's marked as loaded
      const existingFrame = document.getElementById(frameId)

      if (existingFrame && existingFrame.hasAttribute('data-broadcast-dedup-loaded')) {
        const isLoaded = existingFrame.getAttribute('data-broadcast-dedup-loaded') === 'true'

        if (isLoaded) {
          this.log(`Preventing duplicate frame: ${frameId} (already loaded in this window)`)
          event.preventDefault()
          return
        }
      }

      this.log(`Allowing frame: ${frameId} (not yet loaded in this window)`)
    })
  }

  log(message) {
    if (this.debugValue) {
      console.log(`[BroadcastDedup]`, message)
    }
  }
}
